import type { ProjectState } from "@livcg/schema";
import { useEffect, useRef, useState } from "react";
import { api } from "../../shared/api.js";
import { useFetch } from "../../shared/useFetch.js";
import {
  buildOverlayExtras,
  type MatchDetailLite,
  type StandingsCatalogRow,
} from "./overlayData.js";

export type OverlayExtrasBundle = ReturnType<typeof useOverlayExtras>;

/** Shared by OverlayBoard and Deck: catalog detail (round/goals), the
 *  tournament table and the custom BIG text — the overlayDataFor extras. */
export function useOverlayExtras(state: ProjectState) {
  // Catalog data for MATCHID (round/date) and BIG (goal list) — not in the
  // replicant. Re-fetched when the score changes (new goals) or after a PFL resync.
  const detail = useFetch<MatchDetailLite>(`/api/catalog/matches/${state.matchId}`);
  const { refetch } = detail;
  useEffect(() => {
    refetch();
  }, [refetch, state.match.homeScore, state.match.awayScore, state.catalogRev]);

  // Tournament table — not in the replicant, from the catalog (by the match's internal
  // tournament id). The sync trigger requires the PFL id (t_ is stripped from the internal id).
  const tournamentId = detail.data?.match.tournamentId ?? null;
  const standings = useFetch<StandingsCatalogRow[]>(
    tournamentId ? `/api/catalog/standings?tournamentId=${encodeURIComponent(tournamentId)}` : null,
  );
  // Auto-sync bumps catalogRev on every hydrate — refetch the table then too.
  const refetchStandings = standings.refetch;
  const seenRev = useRef(state.catalogRev);
  useEffect(() => {
    if (seenRev.current === state.catalogRev) return;
    seenRev.current = state.catalogRev;
    refetchStandings();
  }, [refetchStandings, state.catalogRev]);

  const [syncing, setSyncing] = useState(false);
  const syncStandings = async (): Promise<void> => {
    if (!tournamentId || syncing) return;
    setSyncing(true);
    try {
      await api.post(
        `/api/sync/standings?tournamentId=${encodeURIComponent(tournamentId.replace(/^t_/, ""))}`,
      );
      standings.refetch();
    } catch {
      /* error — visible in the console; UI is minimal */
    } finally {
      setSyncing(false);
    }
  };

  // Custom BIG text — not in the replicant, panels-local (stored in localStorage).
  const [customText, setCustomText] = useState<string>(
    () => localStorage.getItem("livcg-custom-big-text") ?? "",
  );
  const onCustomText = (v: string) => {
    setCustomText(v);
    try {
      localStorage.setItem("livcg-custom-big-text", v);
    } catch {
      /* private mode — ignore */
    }
  };

  // Overlay extras (catalog roster/goals + custom text) — computed at render time.
  // Not an effect dependency (used only in the toggle closure and the disabled computation),
  // so being a new object on every render doesn't cause a loop.
  const extras = {
    ...buildOverlayExtras(detail.data),
    customText,
    ...(standings.data && { standings: standings.data }),
  };

  return {
    extras,
    customText,
    setCustomText: onCustomText,
    standingsInfo: {
      count: standings.data?.length ?? 0,
      syncing,
      canSync: Boolean(tournamentId),
      sync: () => void syncStandings(),
    },
  };
}
